import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';

interface Document {
  id: string;
  type: string;
  reference: string;
  date: string;
  file_name?: string;
  file_url?: string;
}

interface DocumentsTabProps {
  data: Document[];
  onChange: (data: Document[]) => void;
}

const documentTypes = [
  { value: 'commercial_invoice', label: 'Commercial Invoice' },
  { value: 'packing_list', label: 'Packing List' },
  { value: 'bill_of_lading', label: 'Bill of Lading' },
  { value: 'air_waybill', label: 'Air Waybill' },
  { value: 'certificate_of_origin', label: 'Certificate of Origin' },
  { value: 'import_license', label: 'Import License' },
  { value: 'export_license', label: 'Export License' },
  { value: 'insurance_certificate', label: 'Insurance Certificate' },
  { value: 'inspection_certificate', label: 'Inspection Certificate' },
  { value: 'health_certificate', label: 'Health Certificate' },
  { value: 'phytosanitary_certificate', label: 'Phytosanitary Certificate' },
  { value: 'other', label: 'Other Document' }
];

const DocumentsTab: React.FC<DocumentsTabProps> = ({ data, onChange }) => {
  const { t, tString } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [newDoc, setNewDoc] = useState<Partial<Document>>({
    type: 'commercial_invoice',
    reference: '',
    date: new Date().toISOString().split('T')[0]
  });

  const handleAddDocument = () => {
    if (!newDoc.type || !newDoc.reference) return;

    const document: Document = {
      id: Date.now().toString(),
      type: newDoc.type,
      reference: newDoc.reference,
      date: newDoc.date || new Date().toISOString().split('T')[0],
      file_name: newDoc.file_name
    };

    onChange([...(data || []), document]);
    setOpenDialog(false);
    setNewDoc({
      type: 'commercial_invoice',
      reference: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleDeleteDocument = (id: string) => {
    onChange(data.filter(doc => doc.id !== id));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewDoc(prev => ({
        ...prev,
        file_name: file.name
      }));
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    return documentTypes.find(dt => dt.value === type)?.label || type;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <SectionHeader
          title={t('customsDeclaration.documents.title') as string}
          subtitle={t('customsDeclaration.documents.subtitle') as string}
          icon={<DescriptionIcon color="primary" sx={{ fontSize: 32 }} />}
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Document
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Documents List */}
      {data && data.length > 0 ? (
        <List>
          {data.map((doc) => (
            <Paper key={doc.id} variant="outlined" sx={{ mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">
                        {getDocumentTypeLabel(doc.type)}
                      </Typography>
                      <Chip
                        label={doc.type}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Reference: {doc.reference}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Date: {doc.date}
                      </Typography>
                      {doc.file_name && (
                        <Typography variant="body2" color="text.secondary">
                          File: {doc.file_name}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {doc.file_url && (
                    <>
                      <IconButton edge="end" sx={{ mr: 1 }}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton edge="end" sx={{ mr: 1 }}>
                        <DownloadIcon />
                      </IconButton>
                    </>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteDocument(doc.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No documents added yet. Click "Add Document" to begin.
          </Typography>
        </Paper>
      )}

      {/* Add Document Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Supporting Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Document Type"
                value={newDoc.type || 'commercial_invoice'}
                onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                required
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Reference Number"
                value={newDoc.reference || ''}
                onChange={(e) => setNewDoc({ ...newDoc, reference: e.target.value })}
                required
                helperText="Invoice number, BL number, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Document Date"
                value={newDoc.date || ''}
                onChange={(e) => setNewDoc({ ...newDoc, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadFileIcon />}
              >
                {newDoc.file_name || 'Upload Document (Optional)'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Supported formats: PDF, JPG, PNG (Max 10MB)
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddDocument}
            variant="contained"
            disabled={!newDoc.type || !newDoc.reference}
          >
            Add Document
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsTab;
